<template>
  <header
    class="
      tw-flex tw-items-center tw-px-6 tw-py-3
      tw-h-[var(--secondary-subheader-height)]
    "
  >
    <h3 class="tw-text-base">
      {{ I18n.t('file_attachments_sidebar.header.title') }}
      <span v-if="attachments.length">({{ attachments.length }})</span>
    </h3>

    <div class="tw-ml-auto">
      <SfButtonIcon icon="xmark" @click="drawerStore.closeDrawer" />
    </div>
  </header>

  <div class="tw-px-6 tw-pb-4">
    <AttachmentList
      :attachments="attachments"
      :changes-are-disabled="changesAreDisabled"
      @delete-attachment="deleteAttachment"
    />
  </div>
</template>

<script setup lang="ts">
import { FileAttachmentsDrawerState } from '@app/shared/types/drawer'
import { Attachment } from './types'

import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button'
import { useDrawer } from '@app/shared/pinia/drawer'
import useI18n from '@app/shared/vue/composables/use-I18n'
import AttachmentList from './attachment-list.vue'

const I18n = useI18n()
const drawerStore = useDrawer()
const drawer = drawerStore.open as FileAttachmentsDrawerState
const attachments = ref([])

// The current system cannot save simultaneous changes to the attachments
// so we need to block new changes until the current one is saved
// and the attachments are reloaded
const changesAreDisabled = ref(false)

function deleteAttachment(attachment: Attachment) {
  changesAreDisabled.value = true

  drawer.options.updateAttachments(
    attachments.value,
    [attachment.document_id],
    null,
    () => {
      changesAreDisabled.value = false
    }
  )
}

function loadAttachments() {
  const textPropertyElement = document.querySelector(
    `[data-name="${drawer.options.textPropertyName}"]`
  ) as HTMLElement

  attachments.value = JSON.parse(textPropertyElement.dataset.value || '[]')

  changesAreDisabled.value = false
}

loadAttachments()

function handleUnreviewConfirmationCancelled() {
  changesAreDisabled.value = false
}

watch(
  () => drawerStore.open,
  (newDrawerState) => {
    drawer.options = (newDrawerState as FileAttachmentsDrawerState).options
    loadAttachments()
  }
)

onMounted(() => {
  window.eventHub.$on('sf-live-inputs:savedChanges', loadAttachments)
  window.eventHub.$on(
    'unreview-confirmation-cancelled',
    handleUnreviewConfirmationCancelled
  )
})

onBeforeUnmount(() => {
  window.eventHub.$off('sf-live-inputs:savedChanges', loadAttachments)
  window.eventHub.$on(
    'unreview-confirmation-cancelled',
    handleUnreviewConfirmationCancelled
  )
})
</script>
